import { Deferred, Head, router, usePage } from "@inertiajs/react";
import {
	addMonths,
	format,
	isToday,
	isValid,
	parse,
	startOfDay,
} from "date-fns";
import {
	CalendarIcon,
	Check,
	ChevronDown,
	ChevronsUpDown,
	CopyIcon,
	XIcon,
} from "lucide-react";
import {
	Fragment,
	type ReactNode,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import ChangeViewButton from "@/components/admin-portal/therapist/schedules/change-view-button";
import DayAvailabilityPanel from "@/components/admin-portal/therapist/schedules/day-availability-panel";
import { useDateContext } from "@/components/providers/date-provider";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import { LoadingBasic } from "@/components/shared/loading";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	type TherapistBoardAvailability,
	useScheduleBoardAvailability,
} from "@/hooks/admin-portal/scheduling/use-schedule-board";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useToast } from "@/hooks/use-toast";
import { getDotVariantStatus } from "@/lib/appointments/utils";
import type { GENDERS } from "@/lib/constants";
import { groupLocationsByCountry } from "@/lib/locations";
import {
	countsAsVisit,
	type DayAvailability,
	type DayLoad,
	dayLoad,
	formatClockRange,
	isDayWorthShowing,
} from "@/lib/therapist-scheduling/schedule-board";
import { isBookableStatus } from "@/lib/therapist-scheduling/slot-gating";
import type { AvailabilityStatus } from "@/lib/therapist-scheduling/types";
import { useTherapistEnumLabel } from "@/lib/therapists/enum-label";
import {
	cn,
	copyToClipboard,
	debounce,
	generateInitials,
	populateQueryParams,
} from "@/lib/utils";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { Therapist } from "@/types/admin-portal/therapist";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";

type TherapistsOption = Pick<
	Therapist,
	| "id"
	| "name"
	| "registrationNumber"
	| "gender"
	| "employmentStatus"
	| "employmentType"
	| "service"
	| "user"
	| "modalities"
	| "specializations"
> & {
	activeAddress?: {
		address: string;
		city: string;
		state: string;
		country: string;
	} | null;
};

type TherapistsApptSchedule = Pick<
	Appointment,
	| "id"
	| "appointmentDateTime"
	| "packageId"
	| "patientId"
	| "registrationNumber"
	| "serviceId"
	| "status"
	| "visitNumber"
> & {
	patientGender: (typeof GENDERS)[number];
	packageName: string;
	patientName: string;
	serviceCode: string;
	serviceName: string;
	totalVisit: number;
	addressLine?: string | null;
	location?: {
		id: number;
		country: string;
		country_code: string;
		state: string;
		city: string;
		created_at: string;
		updated_at: string;
	} | null;
};

type TherapistSchedule = Therapist & {
	availabilityByDay?: Record<
		string,
		{
			startTime: string;
			endTime: string;
			freeWindows?: { startTime: string; endTime: string }[];
		}[]
	>;
	appointments?: TherapistsApptSchedule[];
};

type AvailabilitySlots = NonNullable<
	TherapistSchedule["availabilityByDay"]
>[string];

export interface PageProps {
	therapistsOption: {
		data: TherapistsOption[];
	};
	therapistsSchedule: {
		data: TherapistSchedule[];
	};
	filterOptions?: {
		employmentTypes: string[];
		locations: Location[];
	};
}

export interface SchedulesPageGlobalProps
	extends BaseGlobalPageProps,
		PageProps {
	[key: string]: any;
}

// * LocationAccordion component
interface LocationAccordionProps {
	appointment: TherapistsApptSchedule;
}

function LocationAccordion({ appointment }: LocationAccordionProps) {
	const { toast } = useToast();
	const locationLine = appointment?.location?.city || "N/A";
	const addressLine = appointment?.addressLine;

	// Only show accordion if there's more detailed address information
	const hasDetailedAddress = addressLine && addressLine !== locationLine;

	if (!hasDetailedAddress) {
		return <TableCell>{locationLine}</TableCell>;
	}

	return (
		<Accordion type="single" collapsible className="w-full">
			<AccordionItem value="address" className="border-none">
				<AccordionTrigger className="hover:no-underline text-left pt-1 pb-2">
					<span className="text-sm font-normal">{locationLine}</span>
				</AccordionTrigger>
				<AccordionContent className="pb-2">
					<div className="space-y-2">
						<div className="flex items-start justify-between gap-2">
							<span className="font-medium text-muted-foreground/75 text-pretty flex-1">
								{addressLine}
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 w-6 p-0 hover:bg-muted shrink-0"
								onClick={async (e) => {
									e.preventDefault();
									if (await copyToClipboard(addressLine)) {
										toast({
											description: "Address copied to clipboard successfully",
										});
									}
								}}
							>
								<CopyIcon className="h-3 w-3 shrink-0 opacity-75" />
							</Button>
						</div>
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}

// * upstream availability cell — the day header's right-hand side
//
// Design note: the previous version was six chips of equal weight (contract badge,
// count, status badge, hours, reason, button) competing with the date block on the
// left. Every fact was a pill, so nothing read first.
//
// This version carries the same facts on two channels that answer two different
// questions, so neither repeats the other:
//
//   the METER  — how loaded is this day?      visits drawn against the real cap
//   the COLOUR — can anything still be booked? bookable / closed / not working
//
// `booked_slots` is gone from the cell entirely. It counts candidate start-times
// an appointment overlaps, not visits, so one 2h visit reads as "6/18" and looks
// a third full. It needs a sentence of explanation to be safe, and a sentence
// does not belong in a table cell — it moved to the detail panel, which has room.

/** Meter fill by whether the day can still take a booking. */
function loadTone(status: AvailabilityStatus): string {
	if (isBookableStatus(status)) return "bg-emerald-600";
	if (status === "NOT_WORKING" || status === "UNAVAILABLE") {
		return "bg-muted-foreground/30";
	}
	return "bg-destructive";
}

/**
 * Segmented capacity meter. Square by design: `--radius` is 0 across this app,
 * and a row of hairline blocks reads as a gauge without becoming a chart.
 */
function LoadMeter({ load, tone }: { load: DayLoad; tone: string }) {
	return (
		<span className="flex items-center gap-px" aria-hidden="true">
			{Array.from({ length: load.segments }, (_, index) => (
				<span
					key={`seg-${index}-of-${load.segments}`}
					className={cn(
						"h-2.5 w-1.5 border",
						index < load.filled
							? // past the cap: the overflow segments are the alarm
								index < load.cap
								? `${tone} border-transparent`
								: "bg-destructive border-transparent"
							: "bg-transparent border-border",
					)}
				/>
			))}
		</span>
	);
}

function DayAvailabilityCell({
	availability,
	maxDailyAppointments,
	isDetailOpen,
	onToggleDetail,
}: {
	availability: DayAvailability;
	maxDailyAppointments?: number;
	isDetailOpen: boolean;
	onToggleDetail: () => void;
}) {
	const { t } = useTranslation("therapist-schedules");
	const { locale } = useDateContext();
	const load = dayLoad(availability.visitCount, maxDailyAppointments);
	const tone = loadTone(availability.status);
	const isClosed = !isBookableStatus(availability.status);

	return (
		<div className="flex flex-col items-end gap-0.5 px-2 py-1 min-w-0">
			<div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-xs">
				{availability.workingHours && (
					<span className="tracking-tight tabular-nums text-muted-foreground/70">
						{formatClockRange(availability.workingHours, locale.code)}
					</span>
				)}

				{load && <LoadMeter load={load} tone={tone} />}

				<span
					className={cn(
						"font-semibold tracking-tight tabular-nums",
						isClosed && "text-muted-foreground",
					)}
				>
					{load
						? t("day.availability.load", {
								filled: load.filled,
								cap: load.cap,
							})
						: t("day.availability.visits", { count: availability.visitCount })}
				</span>

				{/* the status word rides beside the ratio rather than on its own line:
				    admins scan for the word, but it stays subordinate to the number */}
				<span
					className={cn(
						"text-[10px] font-medium uppercase tracking-widest",
						isClosed ? "text-destructive" : "text-muted-foreground",
					)}
				>
					{t(`day.availability.status.${availability.status}`)}
				</span>

				<Button
					variant="ghost"
					size="sm"
					className="h-5 px-1 -mr-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground hover:text-foreground"
					onClick={onToggleDetail}
					aria-expanded={isDetailOpen}
				>
					{isDetailOpen ? t("close") : t("day.availability.detail")}
					<ChevronDown
						className={cn(
							"ml-0.5 h-3 w-3 transition-transform duration-150 motion-reduce:transition-none",
							isDetailOpen && "rotate-180",
						)}
					/>
				</Button>
			</div>

			{/* genuine exceptions only — the status word above is not one */}
			{(availability.isOutsideContract || availability.reason) && (
				<p className="flex items-center gap-1.5 text-[10px] leading-tight text-right text-muted-foreground">
					{availability.isOutsideContract && (
						<span className="font-medium tracking-wider uppercase text-destructive">
							{t("day.availability.outside_contract")}
						</span>
					)}
					{availability.reason && (
						<span className="italic line-clamp-1">{availability.reason}</span>
					)}
				</p>
			)}
		</div>
	);
}

// * TableSchedules component
interface TableSchedulesProps {
	dayKey: string;
	appointments: TherapistsApptSchedule[];
	availabilitySlots: AvailabilitySlots;
	/** Upstream cell for this day. Present only on the upstream-sourced board. */
	availability?: DayAvailability;
	/** Set alongside `availability` — enables the on-demand /slots detail panel. */
	therapistId?: string;
	/** The load meter's denominator; absent when the therapist is uncapped. */
	maxDailyAppointments?: number;
}

function TableSchedules({
	dayKey,
	appointments,
	availabilitySlots,
	availability,
	therapistId,
	maxDailyAppointments,
}: TableSchedulesProps) {
	const { t: tappt } = useTranslation("appointments");
	const { t } = useTranslation("therapist-schedules");
	const { locale, tzDate } = useDateContext();
	const { toast } = useToast();
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const isApptToday = isToday(new Date(dayKey));
	const dateLabel =
		dayKey === "unscheduled"
			? t("day.unscheduled")
			: format(new Date(dayKey), "dd", {
					in: tzDate,
					locale,
				});
	const monthAndDayLabel =
		dayKey === "unscheduled"
			? t("day.unscheduled")
			: format(new Date(dayKey), "MMM, EEE", {
					in: tzDate,
					locale,
				});

	return (
		<div key={dayKey} className="border border-border/80">
			<div className="flex justify-between gap-3">
				<div className="inline-block">
					<div
						className={cn(
							"px-1 flex flex-initial items-center gap-1.5 text-xs font-semibold uppercase tracking-wider",
							isApptToday
								? "bg-primary/10 text-primary border border-primary/60"
								: "bg-background text-muted-foreground border border-border/60",
						)}
					>
						<span className="font-bold text-base">{dateLabel}</span>
						<span
							className={cn(
								"text-nowrap",
								isApptToday ? "text-primary" : "text-muted-foreground/80",
							)}
						>
							{monthAndDayLabel}
						</span>
					</div>
				</div>

				{availability && (
					<DayAvailabilityCell
						availability={availability}
						maxDailyAppointments={maxDailyAppointments}
						isDetailOpen={isDetailOpen}
						onToggleDetail={() => setIsDetailOpen((open) => !open)}
					/>
				)}

				{!availability &&
					availabilitySlots.some((s: any) => s.freeWindows?.length) &&
					!availabilitySlots.some((s: any) => s.isUnavailable) && (
						<div className="mt-1 flex flex-wrap gap-1 text-xs text-muted-foreground px-2 justify-end">
							<span className="inline-block mr-1">
								{t("day.available_time")}
							</span>

							{availabilitySlots.flatMap(
								(slot: any) =>
									slot.freeWindows?.map((fw: any) => (
										<Badge
											key={`${dayKey}-fw-${slot.startTime}-${slot.endTime}-${fw.startTime}-${fw.endTime}`}
											variant="outline"
											className="text-[10px] px-1"
										>
											{format(
												new Date(`${dayKey}T${fw.startTime}`),
												locale.code === "id" ? "HH:mm" : "hh:mm a",
											)}{" "}
											&mdash;{" "}
											{format(
												new Date(`${dayKey}T${fw.endTime}`),
												locale.code === "id" ? "HH:mm" : "hh:mm a",
											)}
										</Badge>
									)) || [],
							)}
						</div>
					)}

				{!availability &&
					availabilitySlots.some((s: any) => s.isUnavailable) && (
						<div className="mt-1 flex flex-wrap gap-1 text-xs text-muted-foreground px-2 justify-end">
							<span className="inline-block mr-1">{t("day.unavailable")}</span>
							{availabilitySlots
								.filter((s: any) => s.isUnavailable && s.reason)
								.map((s: any) => (
									<span
										key={s.reason}
										className="font-medium italic line-clamp-2"
									>
										{s.reason}
									</span>
								))}
						</div>
					)}
			</div>

			{isDetailOpen && therapistId && dayKey !== "unscheduled" && (
				<DayAvailabilityPanel therapistId={therapistId} date={dayKey} />
			)}

			{appointments.length === 0 ? (
				<div className="mt-3 text-center text-sm text-muted-foreground/75 pb-2">
					<p>{t("day.no_appointments")}</p>
				</div>
			) : (
				<div className="mt-3 rounded-md border border-border/70 overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-background text-muted-foreground border border-border/60">
								<TableHead>{t("day.table.time")}</TableHead>
								<TableHead>{t("day.table.reg_no")}</TableHead>
								<TableHead>{t("day.table.patient")}</TableHead>
								<TableHead>{t("day.table.visit")}</TableHead>
								<TableHead>{t("day.table.status")}</TableHead>
								<TableHead>{t("day.table.location")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{appointments.map((appt) => {
								const apptTime = appt.appointmentDateTime
									? format(
											new Date(appt.appointmentDateTime),
											locale.code === "id" ? "HH:mm" : "hh:mm a",
											{
												in: tzDate,
												locale,
											},
										)
									: "N/A";
								const genderTitle =
									appt.patientGender === "MALE"
										? "Bapak "
										: appt.patientGender === "FEMALE"
											? "Ibu "
											: "";
								const title = `${genderTitle}${appt.patientName || "(No patient)"}`;
								const statusDotVariant = getDotVariantStatus(appt.status);
								const statusLine = tappt(`statuses.${appt.status}`);
								const regLine = appt?.registrationNumber;
								const packageLine = appt?.packageName;
								const visitLine = appt?.totalVisit
									? `Visit ${appt.visitNumber}/${appt.totalVisit}`
									: null;

								return (
									<TableRow key={appt.id}>
										<TableCell className="font-semibold tracking-tight text-nowrap">
											{apptTime}
										</TableCell>
										<TableCell className="tracking-tight font-mono text-nowrap">
											<div className="flex items-center gap-1">
												<a
													href={`/admin-portal/appointments?registration_number=${appt.registrationNumber}`}
													target="_blank"
													className="text-primary hover:underline"
												>
													#{regLine}
												</a>
												<Button
													variant="ghost"
													size="sm"
													className="h-6 w-6 p-0 hover:bg-muted"
													onClick={async (e) => {
														e.preventDefault();
														if (await copyToClipboard(regLine)) {
															toast({
																description:
																	"Reg. no. copied to clipboard successfully",
															});
														}
													}}
												>
													<CopyIcon className="h-3 w-3 shrink-0 opacity-75" />
												</Button>
											</div>
										</TableCell>
										<TableCell className="leading-tight uppercase">
											<div className="flex items-center gap-1">
												{title}
												<Button
													variant="ghost"
													size="sm"
													className="h-6 w-6 p-0 hover:bg-muted"
													onClick={async (e) => {
														e.preventDefault();
														if (await copyToClipboard(appt.patientName)) {
															toast({
																description:
																	"Patient name copied to clipboard successfully",
															});
														}
													}}
												>
													<CopyIcon className="h-3 w-3 shrink-0 opacity-75" />
												</Button>
											</div>
										</TableCell>
										<TableCell>
											{visitLine} &bull; {packageLine}
										</TableCell>
										<TableCell>
											<DotBadgeWithLabel size="xs" variant={statusDotVariant}>
												<span
													title={statusLine}
													className="text-[0.7rem] tracking-tight uppercase line-clamp-1"
												>
													{statusLine}
												</span>
											</DotBadgeWithLabel>
										</TableCell>
										<TableCell>
											<LocationAccordion appointment={appt} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}

// * badges shown inline on the therapist card before collapsing into "+N"
const BADGE_LIMIT = 3;

// * wrapping badge list on the therapist card, "N/A" when the list is empty
function BadgeList({
	items,
	group,
}: {
	items?: string[];
	group: "modality" | "specialization";
}) {
	const { t } = useTranslation("therapist-schedules");
	const enumLabel = useTherapistEnumLabel();

	if (!items?.length) {
		return <span>{t("therapist_card.not_available")}</span>;
	}

	// keep the card header a predictable height: the rest collapses into "+N",
	// still readable in full through the badge's title
	const shown = items.slice(0, BADGE_LIMIT);
	const rest = items.slice(BADGE_LIMIT);

	return (
		<div className="flex flex-wrap gap-1">
			{shown.map((item) => (
				<Badge key={item} variant="outline" className="text-[10px] font-medium">
					{enumLabel(group, item)}
				</Badge>
			))}
			{!!rest.length && (
				<Badge
					variant="outline"
					className="text-[10px] font-medium text-muted-foreground"
					title={rest.map((item) => enumLabel(group, item)).join(", ")}
				>
					+{rest.length}
				</Badge>
			)}
		</div>
	);
}

// * one labelled row on the therapist card: label beside the value, stacking
// * above it once the row shares a four-column track and loses the width for both
function CardRow({
	label,
	title,
	children,
}: {
	label: string;
	title?: string;
	children: ReactNode;
}) {
	return (
		<div className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] items-baseline gap-x-2 gap-y-0.5 2xl:grid-cols-1">
			<dt className="text-[10px] uppercase tracking-wider leading-tight text-muted-foreground">
				{label}
			</dt>
			<dd className="min-w-0 font-medium break-words" title={title}>
				{children}
			</dd>
		</div>
	);
}

// * therapist schedule column component
interface TherapistScheduleColumnProps {
	therapist: TherapistsOption;
	entry?: TherapistSchedule;
	/** formatted active date filter range, shown in the visit count label */
	dateRangeLabel: string;
	/** the deferred `therapistsSchedule` payload has not landed yet */
	isScheduleLoading: boolean;
	isDateWithinFilter: (key: string) => boolean;
	groupAppointmentsByDay: (
		appointments: TherapistsApptSchedule[],
	) => Record<string, TherapistsApptSchedule[]>;
	globalPageProps: SchedulesPageGlobalProps;
	/** Upstream availability for this row; absent while the flag is off. */
	upstream?: TherapistBoardAvailability;
	isUpstreamLoading: boolean;
}

function TherapistScheduleColumn({
	therapist,
	entry,
	dateRangeLabel,
	isScheduleLoading,
	isDateWithinFilter,
	groupAppointmentsByDay,
	globalPageProps,
	upstream,
	isUpstreamLoading,
}: TherapistScheduleColumnProps) {
	const { t } = useTranslation("therapist-schedules");
	const grouped = groupAppointmentsByDay(entry?.appointments || []);
	const availabilityByDay = entry?.availabilityByDay || {};
	const upstreamByDate = upstream?.byDate;
	// The card's number must agree with the per-day counts the cells show, which
	// come from Go — and Go does not count a no-show as a visit. The block itself
	// is still drawn below, so the no-show stays visible on the board.
	const visitCount = (entry?.appointments || []).filter((appt) =>
		countsAsVisit(appt.status),
	).length;
	const dayKeys = Array.from(
		new Set([
			...Object.keys(grouped),
			...Object.keys(upstreamByDate ?? availabilityByDay),
		]),
	)
		.filter((key) => isDateWithinFilter(key))
		.filter((key) => {
			const hasAppointments = (grouped[key]?.length || 0) > 0;
			const upstreamDay = upstreamByDate?.[key];
			const hasAvailability = upstreamByDate
				? !!upstreamDay && isDayWorthShowing(upstreamDay)
				: (availabilityByDay[key]?.length || 0) > 0;
			return hasAppointments || hasAvailability;
		})
		.sort();

	return (
		<div
			key={therapist.id}
			className="w-full flex-shrink-0 flex flex-col gap-2 snap-start h-[calc(100vh-18rem)] min-h-96"
		>
			<div className="flex items-start gap-3 pb-3 mr-2 text-xs border-b border-border">
				{/* workload anchor: the one number the schedule is scanned for */}
				<div className="flex flex-col items-center shrink-0 w-20">
					<div className="flex flex-col items-center justify-center w-full py-2 border border-border bg-background">
						{isScheduleLoading ? (
							<Skeleton className="h-7 w-8" />
						) : (
							<span className="text-2xl font-bold leading-none tabular-nums">
								{visitCount}
							</span>
						)}
						<span className="mt-1.5 text-[9px] uppercase tracking-widest text-muted-foreground">
							{t("therapist_card.visits_short")}
						</span>
					</div>
					<span className="mt-1 text-[10px] text-center text-muted-foreground">
						{dateRangeLabel}
					</span>
				</div>

				<div className="flex-1 min-w-0 space-y-2">
					<div className="flex items-center gap-2 text-left">
						<Avatar className="w-8 h-8 border rounded-lg bg-muted">
							<AvatarImage src="#" alt={therapist.name} />
							<AvatarFallback
								className={cn(
									"text-xs rounded-lg",
									therapist.user.email ===
										globalPageProps.auth.currentUser?.user.email
										? "bg-primary text-primary-foreground"
										: therapist.user["isOnline?"]
											? "bg-emerald-700 text-white"
											: "",
								)}
							>
								{generateInitials(therapist.name)}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 space-y-0.5 leading-tight min-w-0">
							<p className="font-bold uppercase truncate">
								{therapist.name}
								<span className="ml-2 font-light text-muted-foreground">
									#{therapist.registrationNumber}
								</span>
							</p>
							<p className="text-muted-foreground truncate">
								{therapist.employmentType === "FLAT"
									? "MITRA"
									: therapist.employmentType}
								{" · "}
								{therapist.service.name.replaceAll("_", " ")}
								{" · "}
								{therapist.gender}
							</p>
						</div>
					</div>

					<dl className="grid gap-x-8 gap-y-1.5 lg:grid-cols-2 2xl:grid-cols-4">
						<CardRow label={t("therapist_card.modalities")}>
							<BadgeList items={therapist.modalities} group="modality" />
						</CardRow>

						<CardRow label={t("therapist_card.specializations")}>
							<BadgeList
								items={therapist.specializations}
								group="specialization"
							/>
						</CardRow>

						<CardRow
							label={t("therapist_card.domicile")}
							title={therapist.activeAddress?.address}
						>
							<span className="line-clamp-2">
								{therapist.activeAddress?.address ||
									t("therapist_card.not_available")}
							</span>
						</CardRow>

						<CardRow label={t("therapist_card.location")}>
							{therapist.activeAddress?.city ||
								t("therapist_card.not_available")}
						</CardRow>

						{/* only known under bypass_contract_check, i.e. the upstream board —
						    the reason out-of-contract days can now be shown at all */}
						{upstream &&
							(upstream.contractStartDate || upstream.contractEndDate) && (
								<CardRow label={t("therapist_card.contract")}>
									{upstream.contractStartDate ?? "-"} &mdash;{" "}
									{upstream.contractEndDate ?? "-"}
								</CardRow>
							)}
					</dl>
				</div>
			</div>

			<div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-2">
				{/* upstream being down costs the availability markers, not the visit
				    list — that comes from our own database */}
				{upstream?.error && (
					<div className="p-2 text-sm border bg-background text-muted-foreground border-border/60">
						{t("day.availability.unavailable_service")}
					</div>
				)}

				{dayKeys.length === 0 && !isUpstreamLoading && (
					<div className="p-2 bg-background text-sm text-muted-foreground border border-border/60">
						{t("empty.out_of_range")}
					</div>
				)}

				{dayKeys.map((dayKey) => (
					<TableSchedules
						key={dayKey}
						dayKey={dayKey}
						appointments={grouped[dayKey] || []}
						availabilitySlots={availabilityByDay[dayKey] || []}
						availability={upstreamByDate?.[dayKey]}
						therapistId={upstreamByDate ? String(therapist.id) : undefined}
						maxDailyAppointments={
							upstream?.scheduleConfig?.max_daily_appointments
						}
					/>
				))}
			</div>
		</div>
	);
}

// * core component
export default function SchedulesPage({
	therapistsOption,
	therapistsSchedule,
	filterOptions,
}: PageProps) {
	const { props: globalPageProps, url: pageURL } =
		usePage<SchedulesPageGlobalProps>();
	const { currentQuery } = globalPageProps.adminPortal;
	const { locale, tzDate } = useDateContext();
	const { t } = useTranslation("therapist-schedules");

	// ============================================================================
	// URL PARAMETER PARSING
	// ============================================================================

	// Parse URL parameters into filter-friendly format
	const parsedFilters = useMemo(() => {
		// Parse comma-separated therapist IDs from URL
		const parseTherapists = (query?: string | string[]): string[] => {
			if (Array.isArray(query)) return query.map(String);
			if (typeof query === "string") {
				return query
					.split(",")
					.map((item) => item.trim())
					.filter(Boolean);
			}
			return [];
		};

		// Parse single employment type from URL (take first value if multiple)
		const parseEmploymentType = (query?: string | string[]): string[] => {
			if (Array.isArray(query)) return [String(query[0])];
			if (typeof query === "string") {
				const values = query
					.split(",")
					.map((item) => item.trim())
					.filter(Boolean);
				return values.length > 0 ? [values[0]] : [];
			}
			return [];
		};

		// Parse region from URL (single string value)
		const parseRegion = (query?: string): string => {
			if (typeof query === "string" && query.trim()) {
				return query.trim();
			}
			return "";
		};

		return {
			therapists: parseTherapists(currentQuery?.therapists),
			employmentType: parseEmploymentType(currentQuery?.employmentType),
			region: parseRegion(currentQuery?.region),
		};
	}, [
		currentQuery?.therapists,
		currentQuery?.employmentType,
		currentQuery?.region,
	]);

	// ============================================================================
	// DATE UTILITIES
	// ============================================================================

	// Helper to parse date strings with fallback
	const parseDateOrFallback = (
		value: string | undefined,
		fallback: Date,
	): Date => {
		if (!value) return startOfDay(fallback);
		const parsed = parse(value, "dd-MM-yyyy", fallback, { in: tzDate, locale });
		return startOfDay(isValid(parsed) ? parsed : fallback);
	};

	// ============================================================================
	// FILTER STATE MANAGEMENT
	// ============================================================================

	// Main filters state - controls all filtering logic
	const [filters, setFilters] = useState<{
		therapists: string[];
		date: DateRange | undefined;
		employmentType: string[];
		region: string;
	}>({
		therapists: parsedFilters.therapists,
		date: {
			from: parseDateOrFallback(
				currentQuery?.dateFrom as string | undefined,
				new Date(),
			),
			to: parseDateOrFallback(
				currentQuery?.dateTo as string | undefined,
				addMonths(new Date(), 1, { in: tzDate }),
			),
		},
		employmentType: parsedFilters.employmentType,
		region: parsedFilters.region,
	});

	// Filter validation alerts - shows user-friendly error messages
	const [filterAlerts, setFilterAlerts] = useState<{
		therapists: string | null;
		date: string | null;
		employmentType: string | null;
		region: string | null;
	}>({
		therapists: null,
		date: null,
		employmentType: null,
		region: null,
	});

	// Track previous therapist selection for change detection
	const therapistsSnapshotRef = useRef(filters.therapists.join(","));

	// ============================================================================
	// URL SYNCHRONIZATION
	// ============================================================================

	// Debounced URL updater to prevent excessive navigation
	const updateQueryParams = useCallback(
		debounce((url) => {
			router.get(
				url,
				{},
				{
					preserveState: true,
					replace: true,
					only: ["adminPortal", "therapistsSchedule", "therapistsOption"],
				},
			);
		}, 500),
		[],
	);

	// Apply all filters to URL with proper formatting
	const applyFiltersToUrl = useCallback(
		(
			therapists: string[],
			dateRange: DateRange | undefined,
			employmentType: string[] = [],
			region: string = "",
		) => {
			// Format dates for URL
			const formatDate = (date: Date) =>
				format(date, "dd-MM-yyyy", { in: tzDate, locale });

			const formattedFrom = dateRange?.from
				? formatDate(dateRange.from)
				: undefined;
			const formattedTo = dateRange?.to ? formatDate(dateRange.to) : undefined;

			// Update therapist snapshot for change detection
			therapistsSnapshotRef.current = therapists.join(",");

			// Update filter alerts
			setFilterAlerts((prevAlerts) => ({
				...prevAlerts,
				therapists: therapists.length
					? null
					: t("filters.therapist.alert", {
							defaultValue:
								"Choose one or more therapists to preview their schedules side by side.",
						}),
				date:
					!dateRange?.from || !dateRange?.to
						? t("filters.date.alert", {
								defaultValue:
									"Select a valid date range to view therapist schedules.",
							})
						: null,
			}));

			// Build URL with all filter parameters
			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					therapists: therapists.length ? therapists.join(",") : undefined,
					dateFrom: formattedFrom,
					dateTo: formattedTo,
					employmentType: employmentType.length
						? employmentType.join(",")
						: undefined,
					region: region || undefined,
				}),
			);

			updateQueryParams(fullUrl);
		},
		[pageURL, t, updateQueryParams, tzDate, locale],
	);

	// ============================================================================
	// DATA FILTERING & PROCESSING
	// ============================================================================

	// Sort therapist options: selected therapists first, then alphabetically
	const sortedTherapistOptions = useMemo(() => {
		if (!therapistsOption?.data) return [];

		// Data is already filtered by backend, just sort by selection
		return [...therapistsOption.data].sort((a, b) => {
			const aSelected = filters.therapists.includes(String(a.id));
			const bSelected = filters.therapists.includes(String(b.id));

			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;
			return 0;
		});
	}, [therapistsOption?.data, filters.therapists]);

	// Get only the therapists that are currently selected
	const selectedTherapistOptions = useMemo(
		() =>
			sortedTherapistOptions.filter((option) =>
				filters.therapists.includes(String(option.id)),
			),
		[filters.therapists, sortedTherapistOptions],
	);

	// Active date filter, formatted for the visit count label
	const dateRangeLabel = useMemo(() => {
		const formatShort = (date: Date) =>
			format(date, "dd MMM", { in: tzDate, locale });

		if (!filters.date?.from) return "";
		return filters.date.to
			? `${formatShort(filters.date.from)} - ${formatShort(filters.date.to)}`
			: formatShort(filters.date.from);
	}, [filters.date, tzDate, locale]);

	// Build schedule data structure for selected therapists
	const schedulesByTherapist = useMemo(() => {
		const map: Record<string, TherapistSchedule> = {};
		const source = therapistsSchedule?.data ?? [];

		// Process schedule data for selected therapists
		for (const therapist of source) {
			if (!filters.therapists.includes(String(therapist.id))) continue;

			// Sort appointments by date
			const sortedAppointments = [...(therapist.appointments || [])].sort(
				(a, b) =>
					new Date(a.appointmentDateTime || 0).getTime() -
					new Date(b.appointmentDateTime || 0).getTime(),
			);

			map[String(therapist.id)] = {
				...therapist,
				appointments: sortedAppointments,
			};
		}

		// Ensure all selected therapists have entries (even with no schedule data)
		for (const therapist of selectedTherapistOptions) {
			const id = String(therapist.id);
			if (!map[id]) {
				map[id] = {
					...therapist,
					appointments: [],
					availabilityByDay: {},
				} as unknown as TherapistSchedule;
			}
		}

		return map;
	}, [filters.therapists, therapistsSchedule?.data, selectedTherapistOptions]);

	// ============================================================================
	// DATE UTILITIES & HELPERS
	// ============================================================================

	// Check if a date falls within the selected date range filter
	const isDateWithinFilter = useCallback(
		(dateString: string) => {
			if (dateString === "unscheduled") return true;

			const dateObj = new Date(dateString);
			if (!isValid(dateObj)) return false;

			const normalizedDate = startOfDay(dateObj);
			const from = filters.date?.from
				? startOfDay(filters.date.from)
				: undefined;
			const to = filters.date?.to ? startOfDay(filters.date.to) : undefined;

			if (from && normalizedDate < from) return false;
			if (to && normalizedDate > to) return false;
			return true;
		},
		[filters.date?.from, filters.date?.to],
	);

	// Group appointments by day for calendar display
	const groupAppointmentsByDay = useCallback(
		(appointments: TherapistsApptSchedule[]) => {
			return appointments.reduce(
				(acc: Record<string, TherapistsApptSchedule[]>, appt) => {
					const key = appt.appointmentDateTime
						? format(new Date(appt.appointmentDateTime), "yyyy-MM-dd", {
								in: tzDate,
								locale,
							})
						: "unscheduled";
					acc[key] = acc[key] || [];
					acc[key].push(appt);
					return acc;
				},
				{},
			);
		},
		[tzDate, locale],
	);

	// ============================================================================
	// UPSTREAM AVAILABILITY
	// ============================================================================

	// Behind the flag, availability stops being recomputed in Ruby and comes from
	// the Go endpoints the booking engine itself decides with — so what an admin
	// sees on this board and what a booking is allowed to do can no longer drift.
	const useUpstream =
		!!globalPageProps.adminPortal.featureFlags
			?.useUpstreamTherapistSchedulesEnabled;
	const selectedTherapistIds = useMemo(
		() => selectedTherapistOptions.map((option) => String(option.id)),
		[selectedTherapistOptions],
	);
	const { byTherapist: upstreamByTherapist, isLoading: isUpstreamLoading } =
		useScheduleBoardAvailability(
			selectedTherapistIds,
			filters.date?.from
				? format(filters.date.from, "yyyy-MM-dd", { in: tzDate, locale })
				: undefined,
			filters.date?.to
				? format(filters.date.to, "yyyy-MM-dd", { in: tzDate, locale })
				: undefined,
			useUpstream,
		);

	// Split selected therapists into first and additional for layout
	const [firstSelectedTherapist, extraSelectedTherapists] = useMemo(() => {
		const [first, ...rest] = selectedTherapistOptions || [];
		return [first, rest];
	}, [selectedTherapistOptions]);

	// ============================================================================
	// REGION FILTER DATA
	// ============================================================================

	// Get grouped locations from backend
	const groupedLocations = useMemo(() => {
		// Use backend locations or return empty array
		if (filterOptions?.locations && filterOptions.locations.length > 0) {
			return groupLocationsByCountry((filterOptions?.locations as any) || []);
		}

		return [];
	}, [filterOptions?.locations]);

	// Handle therapist popover close event - validates selection and syncs URL
	const handleTherapistPopoverChange = useCallback(
		(open: boolean) => {
			if (open) return;

			const serializedTherapists = filters.therapists.join(",");
			const therapistsChanged =
				serializedTherapists !== therapistsSnapshotRef.current;

			// Show validation alert if no therapists selected
			if (!filters.therapists.length) {
				setFilterAlerts((prev) => ({
					...prev,
					therapists: t("filters.therapist.alert", {
						defaultValue:
							"Choose one or more therapists to preview their schedules side by side.",
					}),
				}));
			} else {
				setFilterAlerts((prev) => ({ ...prev, therapists: null }));
			}

			// Sync URL if therapist selection changed
			if (therapistsChanged) {
				applyFiltersToUrl(
					filters.therapists,
					filters.date,
					filters.employmentType,
					filters.region,
				);
			}
		},
		[applyFiltersToUrl, filters, t],
	);

	// Toggle therapist selection (add/remove)
	const toggleTherapist = useCallback((therapistId: string) => {
		setFilters((prev) => {
			const exists = prev.therapists.includes(therapistId);
			const updatedTherapists = exists
				? prev.therapists.filter((id) => id !== therapistId)
				: [...prev.therapists, therapistId];

			return { ...prev, therapists: updatedTherapists };
		});
	}, []);

	// Remove specific therapist from selection
	const removeTherapist = useCallback(
		(therapistId: string) => {
			setFilters((prev) => {
				const updatedTherapists = prev.therapists.filter(
					(id) => id !== therapistId,
				);
				applyFiltersToUrl(
					updatedTherapists,
					prev.date,
					prev.employmentType,
					prev.region,
				);
				return { ...prev, therapists: updatedTherapists };
			});
		},
		[applyFiltersToUrl],
	);

	// Keep only first therapist, clear additional selections
	const clearAdditionalTherapists = useCallback(() => {
		setFilters((prev) => {
			const [first, ...rest] = prev.therapists;
			if (rest.length === 0) return prev;

			const nextTherapists = first ? [first] : [];
			applyFiltersToUrl(
				nextTherapists,
				prev.date,
				prev.employmentType,
				prev.region,
			);
			return { ...prev, therapists: nextTherapists };
		});
	}, [applyFiltersToUrl]);

	// Handle date range filter changes
	const onChangeDateRange = useCallback(
		(value: DateRange | undefined) => {
			setFilters({ ...filters, date: value });

			const from = value?.from;
			const to = value?.to;

			// Validate date range
			if (!from || !to) {
				setFilterAlerts((prev) => ({
					...prev,
					date: "Please select both start and end dates to filter.",
				}));
				return;
			}

			setFilterAlerts((prev) => ({ ...prev, date: null }));
			applyFiltersToUrl(
				filters.therapists,
				{ from, to },
				filters.employmentType,
				filters.region,
			);
		},
		[applyFiltersToUrl, filters],
	);

	return (
		<>
			<Head title={t("title")} />

			<PageContainer className="grid overflow-hidden space-y-4">
				<div className="flex justify-between gap-4 flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							{t("title")}
						</h1>
					</div>

					<div>
						<ChangeViewButton
							currentPage="schedules"
							routes={{
								daySchedules:
									globalPageProps.adminPortal.router.adminPortal
										.therapistManagement.daySchedules,
								schedules:
									globalPageProps.adminPortal.router.adminPortal
										.therapistManagement.schedules,
							}}
						/>
					</div>
				</div>

				<Separator className="bg-border" />

				<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-8 gap-4 md:gap-2">
					<div className="col-span-full md:col-span-3 lg:col-span-2 xl:col-span-2">
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									id="date-picker-range"
									className="justify-start px-2.5 w-full h-full"
								>
									<CalendarIcon />
									{filters.date?.from ? (
										filters.date.to ? (
											<>
												{format(filters.date.from, "LLL dd, y", {
													in: tzDate,
													locale,
												})}{" "}
												-{" "}
												{format(filters.date.to, "LLL dd, y", {
													in: tzDate,
													locale,
												})}
											</>
										) : (
											format(filters.date.from, "LLL dd, y", {
												in: tzDate,
												locale,
											})
										)
									) : (
										<span>
											{t("filters.date.placeholder", {
												defaultValue: "Select dates...",
											})}
										</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="start">
								<Calendar
									mode="range"
									defaultMonth={filters.date?.from}
									selected={filters.date}
									onSelect={onChangeDateRange}
									numberOfMonths={2}
								/>
							</PopoverContent>
						</Popover>

						{filterAlerts?.date && (
							<p className="mt-2 text-[0.8rem] font-medium text-destructive">
								{filterAlerts.date}
							</p>
						)}
					</div>

					<div className="col-span-full md:col-span-1 lg:col-span-1 xl:col-span-2">
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"w-full justify-between font-normal bg-background h-full shadow p-2",
										!filters.region && "text-muted-foreground",
									)}
								>
									<p className="truncate">
										{filters.region
											? filters.region
											: t("filters.region.placeholder", {
													defaultValue: "Filter by region...",
												})}
									</p>
									<ChevronsUpDown className="opacity-50" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className="p-0 w-[300px]"
								align="start"
								side="bottom"
							>
								<Command>
									<CommandInput
										placeholder={t("filters.region.search", {
											defaultValue: "Search region...",
										})}
										className="h-9"
									/>
									<CommandList>
										<CommandEmpty>
											{t("filters.region.empty", {
												defaultValue: "No region found.",
											})}
										</CommandEmpty>
										{groupedLocations?.map((location) => (
											<Fragment key={location.country}>
												<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
													{location.country}
												</span>

												{location.states.map((state, stateIndex) => (
													<CommandGroup key={state.name} heading={state.name}>
														{state.cities.map((city) => (
															<CommandItem
																key={city.name}
																value={city.name}
																onSelect={() => {
																	setFilters((prev) => ({
																		...prev,
																		therapists: [], // Reset therapist selection
																		region: city.name || "",
																	}));
																	applyFiltersToUrl(
																		[], // Pass empty therapists array
																		filters.date,
																		filters.employmentType,
																		city.name || "",
																	);
																}}
															>
																<span>{city.name}</span>
																<Check
																	className={cn(
																		"ml-auto",
																		city.name === filters.region
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</CommandItem>
														))}
														{location.states.length !== stateIndex + 1 && (
															<CommandSeparator className="mt-2" />
														)}
													</CommandGroup>
												))}
											</Fragment>
										))}
									</CommandList>
									<CommandList>
										<CommandGroup>
											<CommandSeparator className="mb-2" />
											<CommandItem
												onSelect={() => {
													setFilters((prev) => ({
														...prev,
														therapists: [], // Reset therapist selection
														region: "",
													}));
													applyFiltersToUrl(
														[], // Pass empty therapists array
														filters.date,
														filters.employmentType,
														"",
													);
												}}
											>
												<span className="mx-auto font-medium text-center uppercase w-fit">
													{t("filters.region.clear", {
														defaultValue: "Clear",
													})}
												</span>
											</CommandItem>
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>

						{filterAlerts?.region && (
							<p className="mt-2 text-[0.8rem] font-medium text-destructive">
								{filterAlerts.region}
							</p>
						)}
					</div>

					<div className="col-span-full md:col-span-2 lg:col-span-1 xl:col-span-1">
						<Select
							value={filters.employmentType[0] || "all"}
							onValueChange={(value) => {
								const newEmploymentType = value === "all" ? [] : [value];
								setFilters((prev) => ({
									...prev,
									therapists: [], // Reset therapist selection
									employmentType: newEmploymentType,
								}));
								applyFiltersToUrl(
									[], // Pass empty therapists array
									filters.date,
									newEmploymentType,
									filters.region,
								);
							}}
						>
							<SelectTrigger className="h-full font-normal bg-background shadow">
								<SelectValue
									placeholder={t("filters.employmentType.placeholder", {
										defaultValue: "Filter by employment type...",
									})}
								/>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">
									{t("filters.employmentType.all", {
										defaultValue: "All",
									})}
								</SelectItem>
								{filterOptions?.employmentTypes?.map((type) => (
									<SelectItem key={type} value={type}>
										<span className="text-sm uppercase tracking-tight">
											{type}
										</span>
									</SelectItem>
								)) || []}
							</SelectContent>
						</Select>

						{filterAlerts?.employmentType && (
							<p className="mt-2 text-[0.8rem] font-medium text-destructive">
								{filterAlerts.employmentType}
							</p>
						)}
					</div>

					<div className="col-span-full md:col-span-3 lg:col-span-2 xl:col-span-3">
						<Deferred
							data={["therapistsOption"]}
							fallback={<Skeleton className="h-full" />}
						>
							<Popover onOpenChange={handleTherapistPopoverChange}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										className={cn(
											"w-full justify-between font-normal bg-background h-auto shadow min-h-10 p-2",
											selectedTherapistOptions?.length === 0 &&
												"text-muted-foreground",
										)}
									>
										<div className="flex flex-wrap items-center gap-1">
											{firstSelectedTherapist ? (
												<>
													<Badge
														key={firstSelectedTherapist.id}
														variant="accent"
														className="text-xs group"
													>
														{firstSelectedTherapist.name}
														{/* biome-ignore lint/a11y/useSemanticElements: icon as button */}
														<div
															onClick={(e) => {
																e.stopPropagation();
																e.preventDefault();
																removeTherapist(
																	String(firstSelectedTherapist.id),
																);
															}}
															onKeyDown={() => {}}
															className="ml-1 hover:text-destructive opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1 rounded p-0.5 cursor-pointer"
															role="button"
															tabIndex={0}
															aria-label={`Remove ${firstSelectedTherapist.name}`}
														>
															<XIcon className="h-3 w-3" />
														</div>
													</Badge>

													{extraSelectedTherapists.length > 0 && (
														<Badge
															variant="accent"
															className="text-xs flex items-center gap-1"
														>
															+{extraSelectedTherapists.length} more
															{/* biome-ignore lint/a11y/useSemanticElements: icon as button */}
															<div
																onClick={(e) => {
																	e.stopPropagation();
																	e.preventDefault();
																	clearAdditionalTherapists();
																}}
																onKeyDown={() => {}}
																className="ml-1 hover:text-destructive opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1 rounded p-0.5 cursor-pointer"
																role="button"
																tabIndex={0}
																aria-label={`Clear ${extraSelectedTherapists.length} additional therapists`}
															>
																<XIcon className="h-3 w-3" />
															</div>
														</Badge>
													)}
												</>
											) : (
												<span>
													{t("filters.therapist.placeholder", {
														defaultValue: "Select therapists...",
													})}
												</span>
											)}
										</div>
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent
									className="p-0 w-[var(--radix-popover-trigger-width)]"
									align="start"
								>
									<Command>
										<CommandInput
											placeholder={t("filters.therapist.search", {
												defaultValue: "Search therapist...",
											})}
										/>
										<CommandList>
											<CommandEmpty>
												{t("filters.therapist.empty", {
													defaultValue: "No therapist found.",
												})}
											</CommandEmpty>
											<CommandGroup>
												{sortedTherapistOptions?.map((therapist) => {
													const isSelected = filters.therapists.includes(
														String(therapist.id),
													);

													return (
														<CommandItem
															key={therapist.id}
															value={therapist.name}
															onSelect={() =>
																toggleTherapist(String(therapist.id))
															}
															className={cn(isSelected && "bg-primary/5")}
														>
															<div className="flex items-center gap-2 flex-1">
																<div className="flex flex-col">
																	<span className="text-sm uppercase tracking-tight">
																		{therapist.name}
																	</span>
																	{therapist?.employmentType &&
																		therapist?.service?.name && (
																			<span className="text-xs text-muted-foreground/75">
																				{therapist.employmentType === "FLAT"
																					? "MITRA"
																					: "KARPIS"}{" "}
																				&bull;{" "}
																				{therapist.service.name.replaceAll(
																					"_",
																					" ",
																				)}
																			</span>
																		)}
																</div>
															</div>
															<div className="flex items-center gap-4">
																<Check
																	className={cn(
																		"h-4 w-4",
																		isSelected ? "opacity-100" : "opacity-0",
																	)}
																/>
															</div>
														</CommandItem>
													);
												})}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						</Deferred>

						{filterAlerts?.therapists && (
							<p className="mt-2 text-[0.8rem] font-medium text-destructive">
								{filterAlerts.therapists}
							</p>
						)}
					</div>
				</div>

				<Deferred
					data={["therapistsOption"]}
					fallback={<LoadingBasic columnBased />}
				>
					{!selectedTherapistOptions?.length ? (
						<div className="flex items-center justify-center px-3 py-8 border rounded-md border-border bg-background text-muted-foreground">
							<div className="text-center space-y-2">
								<h2 className="text-sm font-semibold">{t("empty.title")}</h2>
								<p className="text-xs text-pretty">{t("empty.description")}</p>
							</div>
						</div>
					) : (
						<div className="w-full overflow-x-auto pb-2 snap-x">
							<div className="flex items-start gap-2 min-w-full">
								{selectedTherapistOptions.map((therapist) => (
									<TherapistScheduleColumn
										key={therapist.id}
										therapist={therapist}
										entry={schedulesByTherapist[String(therapist.id)]}
										dateRangeLabel={dateRangeLabel}
										isScheduleLoading={!therapistsSchedule}
										isDateWithinFilter={isDateWithinFilter}
										groupAppointmentsByDay={groupAppointmentsByDay}
										globalPageProps={globalPageProps}
										upstream={
											useUpstream
												? upstreamByTherapist[String(therapist.id)]
												: undefined
										}
										isUpstreamLoading={isUpstreamLoading}
									/>
								))}
							</div>
						</div>
					)}
				</Deferred>
			</PageContainer>
		</>
	);
}
